/* Key numbers: big figures with their change vs the previous period of the same
   length. Cells fill the space (≈150px each, a second row when tall enough); the
   ones that don't fit are hidden and the meta says "n of m shown". */

import { useTranslation } from 'react-i18next';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import { fmt, pick, ratio, usd, usd2, type MoneyMode } from '../../components/Statistics/unified/model';
import type { ChatStatsResponse, StatisticsResponse } from '../../api/client';
import { periodRange, previousRange, useDash, useSource } from '../context';
import type { KpiKey, WidgetOf } from '../model';
import { BodyNote, WidgetFrame, type FrameProps } from '../WidgetFrame';
import { bodySize, useDataState } from '../frameUtils';
import { aiShare, change, ppvSoldCount, useMetaText } from './shared';

const CHAT_KEYS: KpiKey[] = ['chats', 'rpc'];
const STATS_KEYS: KpiKey[] = ['rev', 'ai', 'airev', 'rpc', 'ppv', 'tips'];
/** Numbers whose change needs the previous window's /statistics (Revenue has `previous`). */
const PREV_STATS_KEYS: KpiKey[] = ['ai', 'airev', 'rpc', 'ppv', 'tips'];

interface Cell {
  key: KpiKey;
  label: string;
  value: string;
  other?: string;
  delta: string;
}

function figures(key: KpiKey, s: StatisticsResponse | null, c: ChatStatsResponse | null, mode: MoneyMode) {
  const m = s?.money;
  const airev = pick(m?.sellers.ai, mode);
  const chats = c?.totals.chats_activated ?? 0;
  switch (key) {
    case 'rev': return pick(m?.revenue, mode);
    case 'ai': return aiShare(m?.sellers, mode);
    case 'airev': return airev;
    case 'chats': return chats;
    case 'rpc': return ratio(airev, chats);
    case 'ppv': return ppvSoldCount(s);
    case 'tips': return pick(m?.revenue.by_type.find((x) => x.type === 'tips'), mode);
  }
}

export function KeyNumbers(p: FrameProps & { widget: WidgetOf<'kpi'> }) {
  const { t } = useTranslation('dashboardWidgets');
  const { t: ts } = useTranslation('statsUnified');
  const { today, money } = useDash();
  const meta = useMetaText();
  const state = useDataState();
  const c = p.widget.cfg;
  const range = periodRange(c.per, today);
  const prev = previousRange(range);
  const keys = c.metrics;
  const stats = useSource('stats', keys.some((k) => STATS_KEYS.includes(k)) ? range : null);
  const chat = useSource('chat', keys.some((k) => CHAT_KEYS.includes(k)) ? range : null);
  const prevStats = useSource('stats', c.delta && keys.some((k) => PREV_STATS_KEYS.includes(k)) ? prev : null);
  const prevChat = useSource('chat', c.delta && keys.some((k) => CHAT_KEYS.includes(k)) ? prev : null);

  const { width, height } = bodySize(p);
  const cols = Math.max(1, Math.min(keys.length, Math.floor(width / 150)));
  const rows = Math.max(1, Math.min(Math.ceil(keys.length / cols), Math.floor(height / 64)));
  const shown = keys.slice(0, cols * rows);
  const cellH = height / rows;
  const valueSize = cellH > 110 ? 32 : cellH > 80 ? 26 : 22;
  const wideCells = width / Math.max(1, Math.min(cols, keys.length)) > 260;

  const needed = [keys.some((k) => STATS_KEYS.includes(k)) && stats, keys.some((k) => CHAT_KEYS.includes(k)) && chat].filter((x): x is typeof stats => !!x);
  const blocked = needed.find((x) => x.noAccess) ?? needed.find((x) => x.error) ?? needed.find((x) => !x.data);
  const other: MoneyMode = money === 'gross' ? 'net' : 'gross';

  const cells: Cell[] = shown.map((key) => {
    const cur = figures(key, stats.data, chat.data, money);
    let value = '—';
    let delta = '';
    if (cur != null) {
      value = key === 'ai' ? `${Math.round(cur)}%` : key === 'rpc' ? usd2(cur) : key === 'chats' || key === 'ppv' ? fmt(cur) : usd(cur);
    }
    if (c.delta) {
      if (key === 'rev') delta = change(cur ?? 0, stats.data ? pick(stats.data.money.previous, money) : null);
      else if (key === 'ai') {
        const was = prevStats.data ? aiShare(prevStats.data.money.sellers, money) : null;
        if (cur != null && was != null) {
          const pts = Math.round(cur - was);
          delta = t('kpi.pts', { v: pts > 0 ? `+${pts}` : pts < 0 ? `−${Math.abs(pts)}` : '0' });
        }
      } else {
        const ready = (!STATS_KEYS.includes(key) || prevStats.data) && (!CHAT_KEYS.includes(key) || prevChat.data);
        if (ready) delta = change(cur ?? 0, figures(key, prevStats.data, prevChat.data, money));
      }
    }
    return {
      key,
      label: t(`kpi.${key}`),
      value,
      other: key === 'rev' && stats.data ? `${ts(`money.${other}`)} ${usd(pick(stats.data.money.revenue, other))}` : undefined,
      delta,
    };
  });

  const metaText = shown.length < keys.length ? t('kpi.shown', { n: shown.length, m: keys.length }) : meta.period(c.per);
  const blockedNote = blocked && state({ error: blocked.error, noAccess: blocked.noAccess, hasData: !!blocked.data, reload: () => needed.forEach((x) => x.reload()) });

  return (
    <WidgetFrame {...p} meta={meta.join([metaText])}>
      {!keys.length ? (
        <BodyNote>{t('kpi.empty')}</BodyNote>
      ) : blockedNote ? (
        blockedNote
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${Math.min(cols, shown.length) || 1}, minmax(0, 1fr))`, gridAutoRows: 'minmax(0, 1fr)', gap: 1, background: C.border }}>
          {cells.map((cell) => (
            <div key={cell.key} style={{ background: C.card, padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3, minWidth: 0 }}>
              <span style={{ font: f(500, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {cell.label}
                {/* No title bar: the period (and scope) ride on the first label instead. */}
                {!p.widget.cfg.hdr && cell === cells[0] && <span style={{ color: C.muted }}>{` · ${meta.join([metaText])}`}</span>}
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                <span style={{ font: f(700, valueSize, 1), color: C.text, whiteSpace: 'nowrap' }}>{cell.value}</span>
                {cell.delta && <span style={{ font: f(600, 12), color: cell.delta.startsWith('−') ? C.sub : C.purpleLt, whiteSpace: 'nowrap' }}>{cell.delta}</span>}
                {cell.other && wideCells && <span style={{ font: f(500, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell.other}</span>}
              </span>
              {/* Headline Revenue always carries both Gross and Net. */}
              {cell.other && !wideCells && <span style={{ font: f(500, 11), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell.other}</span>}
            </div>
          ))}
        </div>
      )}
    </WidgetFrame>
  );
}
